import React, { useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Universal "Are you sure?" confirmation modal.
 * Used for ALL destructive actions across the WP Mgmt UI per project rule:
 *   "Any delete should have double user prompt."
 *
 * Usage:
 *   const [confirmState, setConfirmState] = useState(null);
 *   <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
 *
 *   // To trigger:
 *   setConfirmState({
 *     title: 'Delete this game?',
 *     description: 'This will remove the game from WordPress. This cannot be undone.',
 *     confirmLabel: 'Yes, delete',
 *     destructive: true,
 *     onConfirm: async () => { await doDelete(); }
 *   });
 */
export default function ConfirmDialog({ state, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const {
    title = 'Are you sure?',
    description = 'This action cannot be undone.',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
  } = state;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      if (onConfirm) await onConfirm();
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  return (
    <AlertDialog open={true} onOpenChange={(open) => { if (!open && !busy) onClose?.(); }}>
      <AlertDialogContent
        className="bg-slate-900 border-slate-700 text-slate-100"
        data-testid="confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-slate-100">
            {destructive && <AlertTriangle className="h-5 w-5 text-red-400" />}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"
            disabled={busy}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className={destructive
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-amber-600 hover:bg-amber-700 text-white'}
            disabled={busy}
            onClick={handleConfirm}
            data-testid="confirm-dialog-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
